/**
 * BrokersTab Component
 *
 * Main tab component for broker management within the Portfolio Reference Data page.
 * Provides a two-card vertical layout (desktop only):
 * - Top Card: Broker selector dropdown with actions (New Broker, Show Archived)
 * - Bottom Card: Fee Configuration with resizable two-pane layout
 *
 * Pattern follows StrategiesPlaybooksPage for consistency:
 * - Provider wraps page content (BrokersProvider)
 * - URL sync pushes selection to URL after hydration (desktop only)
 * - Controlled Select with special action keys
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Eye, EyeOff, AlertTriangle, Package, Search, Globe, Maximize2, Minimize2, Settings2 } from 'lucide-react';

import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  ScrollShadow,
  Select,
  SelectItem,
  SelectSection,
  Button,
  Input,
  EmptyState,
  NumberInput,
  TextArea,
  useDisclosure,
  useToastNotification,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
} from '@enfomentis/ui';
import { useIsDesktop } from '@/hooks';
import { getCurrencyInfo } from '@/utils';
import { toDisplayValue, toStorageValue } from '@/utils/moneyHelpers';
import { CURRENCY_DATABASE } from '@/constants';
import {
  ASSET_CLASS_TYPE_DISPLAY_MAP,
  MARKET_SEGMENT_DISPLAY_MAP,
  SUB_ASSET_CLASS_DISPLAY_MAP,
} from '@/constants/assetClass';

import { CurrencyChooserModal } from '@/components/shared/CurrencyChooser/CurrencyChooserModal';
import { BrokersProvider, useBrokers } from './context';
import { BrokerModal } from './BrokerModal';
import { BrokerRenameModal } from './BrokerRenameModal';
import { BrokerImportModal } from './BrokerImportModal';
import { AddCategoryModal } from './AddCategoryModal';
import { AddInstrumentModal } from './AddInstrumentModal';
import { CopyFeeModal } from './CopyFeeModal';
import { BrokerFinancingFXModal } from './BrokerFinancingFXModal';
import { FeeConfigurationEditor } from './feeEditor/FeeConfigurationEditor';
import { FeeTree, buildFeeTree } from './feeTree';
import type { FeeTreeActionType, FeeTreeNode } from './feeTree';
import { findBrokerById } from '@/data/brokerCatalog';
import { ResizablePane } from '@/components/shared/ResizablePane';

// =============================================================================
// Constants
// =============================================================================

/**
 * Special keys for action items in Select dropdown.
 * Prefixed with __ to avoid collision with actual broker IDs.
 */
const NEW_BROKER_KEY = '__new_broker__';
const IMPORT_CATALOG_KEY = '__import_catalog__';
const TOGGLE_ARCHIVED_KEY = '__toggle_archived__';

// =============================================================================
// Helpers
// =============================================================================

/** Collect all node IDs from a fee tree into a Set (for selection validation). */
function collectNodeIds(nodes: FeeTreeNode[]): Set<string> {
  const ids = new Set<string>();
  const walk = (items: FeeTreeNode[]): void => {
    for (const node of items) {
      ids.add(node.id);
      walk(node.children);
    }
  };
  walk(nodes);
  return ids;
}

// =============================================================================
// Inner Content Component
// =============================================================================

/**
 * Inner component that consumes the BrokersContext.
 * Separated to allow the provider to wrap at the appropriate level.
 */
const BrokersTabContent: React.FC = () => {
  // Desktop detection
  const isDesktop = useIsDesktop();

  // ===========================================================================
  // Context Consumption
  // ===========================================================================

  const {
    // Data
    brokers,
    filteredBrokers,
    selectedBroker,
    selectedBrokerId,
    loading,
    linkedActiveAccounts,

    // Filter state
    showArchived,

    // Hydration state
    isHydrating,

    // Dirty state
    localDescription,
    localFeeMetadata,
    localOvernightDefault,
    localConversionDefault,
    isDirty,
    isSaving,
    showConflictBanner,

    // Actions
    selectBroker,
    setShowArchived,
    reloadBrokers,
    setLocalDescription,
    setLocalFeeMetadata,
    setLocalOvernightDefault,
    setLocalConversionDefault,
    saveBroker,
    discardBrokerChanges,
    archiveBroker,
    restoreBroker,
    acceptExternalChanges,
    keepEditing,
    removeFeeConfiguration,
  } = useBrokers();

  // ===========================================================================
  // Computed Values
  // ===========================================================================

  // Separate active and archived brokers for Select sections
  const activeBrokers = useMemo(
    () => brokers.filter((b) => b.status !== 'archived'),
    [brokers]
  );
  const archivedBrokers = useMemo(
    () => brokers.filter((b) => b.status === 'archived'),
    [brokers]
  );

  // Metadata currency/precision derived values
  const metadataCurrencyCode = localFeeMetadata.defaultAccountCurrency ?? 'USD';
  const metadataCurrencyInfo = getCurrencyInfo(metadataCurrencyCode, CURRENCY_DATABASE);
  const metadataPrecision = metadataCurrencyInfo?.minorUnit ?? localFeeMetadata.minimumBalance?.precision ?? 2;
  const metadataCurrencyHint = metadataCurrencyCode;

  // ===========================================================================
  // UI-Only State
  // ===========================================================================

  // Controlled Select state (prevents close when toggle is clicked)
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const toggleClickedRef = useRef(false);

  // Details expansion state (for collapsible CardBody)
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  // Fee configuration card expand/maximize state
  const [isFeeCardExpanded, setIsFeeCardExpanded] = useState(false);

  // Popover state for empty state "New Broker" button
  const [isAddPopoverOpen, setIsAddPopoverOpen] = useState(false);

  // Currency chooser modal state (for metadata account currency)
  const [showCurrencyChooser, setShowCurrencyChooser] = useState(false);

  // Broker-level financing & FX modal state
  const [showFinancingFXModal, setShowFinancingFXModal] = useState(false);

  // Fee tree selection state (drives fee editor)
  const [selectedFeeNodeId, setSelectedFeeNodeId] = useState<string | null>(null);

  // Fee tree search query
  const [feeSearchQuery, setFeeSearchQuery] = useState('');

  // Fee tree remove confirmation state
  const [removingNode, setRemovingNode] = useState<FeeTreeNode | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  // Fee tree add category modal state
  const [addCategoryState, setAddCategoryState] = useState<{ parentCategoryPath: string[] } | null>(null);

  // Fee tree add instrument modal state
  const [addInstrumentState, setAddInstrumentState] = useState<{ parentCategoryPath: string[] } | null>(null);

  // Fee tree copy modal state
  const [copyState, setCopyState] = useState<{ sourceNode: FeeTreeNode } | null>(null);
  const lastCopySourceRef = useRef<FeeTreeNode | null>(null);
  if (copyState) lastCopySourceRef.current = copyState.sourceNode;

  // Fee tree header add popover state
  const [isAddFeePopoverOpen, setIsAddFeePopoverOpen] = useState(false);

  // Fee editor unsaved-change guard state
  const [pendingNodeNavigation, setPendingNodeNavigation] = useState<string | null>(null);
  const feeEditorDirtyRef = useRef(false);
  const feeEditorDiscardRef = useRef<(() => void) | null>(null);
  const feeEditorSaveRef = useRef<(() => Promise<boolean>) | null>(null);
  const [isNavSaving, setIsNavSaving] = useState(false);

  // ===========================================================================
  // Modal State
  // ===========================================================================

  const brokerModalDisclosure = useDisclosure();
  const renameModalDisclosure = useDisclosure();
  const importModalDisclosure = useDisclosure();

  // ===========================================================================
  // URL Sync (Desktop Only) - Push selection to URL
  // ===========================================================================

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    // Guard: only run after hydration, on desktop
    // Mobile uses separate routes — URL sync is desktop only
    if (isHydrating || !isDesktop) return;

    // Get current tab from URL (preserve it)
    const currentTab = searchParams.get('tab');

    // Build new params based on current selection
    const newParams = new URLSearchParams();
    if (currentTab) {
      newParams.set('tab', currentTab);
    }
    if (selectedBrokerId) {
      newParams.set('brokerId', selectedBrokerId);
    }

    // Compare with current URL params to avoid redundant replaceState
    const currentBrokerParam = searchParams.get('brokerId');

    if (currentBrokerParam !== selectedBrokerId) {
      setSearchParams(newParams, { replace: true });
    }
  }, [isHydrating, isDesktop, selectedBrokerId, searchParams, setSearchParams]);

  // ===========================================================================
  // Fee Tree Selection Reset
  // ===========================================================================

  // Reset fee tree and metadata UI state when broker changes
  useEffect(() => {
    setSelectedFeeNodeId(null);
    setFeeSearchQuery('');
    setRemovingNode(null);
    setAddCategoryState(null);
    setAddInstrumentState(null);
    setCopyState(null);
    setIsAddFeePopoverOpen(false);
    setShowCurrencyChooser(false);
    setPendingNodeNavigation(null);
    feeEditorDirtyRef.current = false;
    feeEditorDiscardRef.current = null;
    feeEditorSaveRef.current = null;
    setIsNavSaving(false);
    setShowFinancingFXModal(false);
  }, [selectedBrokerId]);

  // ===========================================================================
  // Selection Reconciliation
  // ===========================================================================

  const displayMaps = useMemo(
    () => ({
      assetClassType: ASSET_CLASS_TYPE_DISPLAY_MAP,
      marketSegment: MARKET_SEGMENT_DISPLAY_MAP,
      subAssetClass: SUB_ASSET_CLASS_DISPLAY_MAP,
    }),
    []
  );

  const validNodeIds = useMemo(() => {
    const configs = selectedBroker?.feeConfigurations ?? [];
    if (configs.length === 0) return new Set<string>();
    const tree = buildFeeTree(configs, displayMaps);
    return collectNodeIds(tree);
  }, [selectedBroker?.feeConfigurations, displayMaps]);

  // Clear stale selection / pending navigation when tree structure changes
  useEffect(() => {
    if (selectedFeeNodeId && !validNodeIds.has(selectedFeeNodeId)) {
      setSelectedFeeNodeId(null);
    }
    if (pendingNodeNavigation && !validNodeIds.has(pendingNodeNavigation)) {
      setPendingNodeNavigation(null);
    }
  }, [selectedFeeNodeId, pendingNodeNavigation, validNodeIds]);

  // ===========================================================================
  // Handlers
  // ===========================================================================

  const handleBrokerSelect = useCallback(
    (keys: 'all' | Set<React.Key>): void => {
      if (keys === 'all') return;
      const selected = Array.from(keys)[0] as string | undefined;
      if (!selected) return;

      // Handle "New Broker" action
      if (selected === NEW_BROKER_KEY) {
        brokerModalDisclosure.onOpen();
        return;
      }

      // Handle "Import from Catalog..." action
      if (selected === IMPORT_CATALOG_KEY) {
        importModalDisclosure.onOpen();
        return;
      }

      // Handle "Show Archived" toggle action
      if (selected === TOGGLE_ARCHIVED_KEY) {
        toggleClickedRef.current = true;
        setShowArchived(!showArchived);
        return;
      }

      // Normal broker selection
      if (selected !== selectedBrokerId) {
        selectBroker(selected);
      }
    },
    [selectedBrokerId, showArchived, setShowArchived, selectBroker, brokerModalDisclosure, importModalDisclosure]
  );

  const handleSelectOpenChange = useCallback((open: boolean): void => {
    if (!open && toggleClickedRef.current) {
      // Toggle was clicked - reset ref and keep Select open
      toggleClickedRef.current = false;
      return;
    }
    setIsSelectOpen(open);
  }, []);

  const handleBrokerModalSave = useCallback(
    async (newBrokerId?: string): Promise<void> => {
      await reloadBrokers();
      if (newBrokerId) {
        selectBroker(newBrokerId);
      }
    },
    [reloadBrokers, selectBroker]
  );

  const handleRenameModalSave = useCallback(async (): Promise<void> => {
    await reloadBrokers();
  }, [reloadBrokers]);

  const handleImportComplete = useCallback(
    async (brokerId: string): Promise<void> => {
      await reloadBrokers();
      selectBroker(brokerId);
    },
    [reloadBrokers, selectBroker]
  );

  // Metadata currency change handler
  const handleMetadataCurrencyChange = useCallback(
    (currencyCode: string): void => {
      const newInfo = getCurrencyInfo(currencyCode, CURRENCY_DATABASE);
      const newPrecision = newInfo?.minorUnit ?? 2;
      setLocalFeeMetadata({
        ...localFeeMetadata,
        defaultAccountCurrency: currencyCode,
        minimumBalance: {
          amount: localFeeMetadata.minimumBalance?.amount ?? 0,
          precision: newPrecision,
          currency: currencyCode,
        },
      });
      setShowCurrencyChooser(false);
    },
    [localFeeMetadata, setLocalFeeMetadata]
  );

  // Financing & FX modal confirm handler
  const handleFinancingFXConfirm = useCallback(
    (
      overnight: Parameters<typeof setLocalOvernightDefault>[0],
      conversion: Parameters<typeof setLocalConversionDefault>[0],
    ): void => {
      setLocalOvernightDefault(overnight);
      setLocalConversionDefault(conversion);
      setShowFinancingFXModal(false);
    },
    [setLocalOvernightDefault, setLocalConversionDefault]
  );

  // Popover action handlers (for empty state)
  const handleAddPopoverCreateNew = useCallback((): void => {
    setIsAddPopoverOpen(false);
    brokerModalDisclosure.onOpen();
  }, [brokerModalDisclosure]);

  const handleAddPopoverImport = useCallback((): void => {
    setIsAddPopoverOpen(false);
    importModalDisclosure.onOpen();
  }, [importModalDisclosure]);

  // Fee tree node selection handler (guarded by unsaved-change policy)
  const handleFeeNodeSelect = useCallback((nodeId: string | null): void => {
    if (feeEditorDirtyRef.current && nodeId !== null) {
      // Block navigation — show confirmation modal
      setPendingNodeNavigation(nodeId);
      return;
    }
    setSelectedFeeNodeId(nodeId);
  }, []);

  // Unsaved-change confirmation handlers
  const handleUnsavedDiscard = useCallback((): void => {
    feeEditorDiscardRef.current?.();
    if (pendingNodeNavigation !== null) {
      setSelectedFeeNodeId(pendingNodeNavigation);
    }
    setPendingNodeNavigation(null);
  }, [pendingNodeNavigation]);

  const handleUnsavedCancel = useCallback((): void => {
    setPendingNodeNavigation(null);
  }, []);

  // Toast notification
  const { showToast } = useToastNotification();

  const handleUnsavedSave = useCallback(async (): Promise<void> => {
    if (feeEditorSaveRef.current === null) {
      showToast({ title: 'Save unavailable', color: 'danger' });
      return;
    }
    setIsNavSaving(true);
    try {
      const success = await feeEditorSaveRef.current();
      if (success) {
        if (pendingNodeNavigation !== null) {
          setSelectedFeeNodeId(pendingNodeNavigation);
        }
        setPendingNodeNavigation(null);
      }
      // On failure: keep modal open (user can retry or discard)
    } finally {
      setIsNavSaving(false);
    }
  }, [pendingNodeNavigation, showToast]);

  // Fee tree row action handler (kebab menu)
  const handleFeeAction = useCallback(
    (action: FeeTreeActionType, node: FeeTreeNode): void => {
      switch (action) {
        case 'add-child':
          setAddCategoryState({ parentCategoryPath: node.categoryPath });
          break;
        case 'add-instrument':
          setAddInstrumentState({ parentCategoryPath: node.categoryPath });
          break;
        case 'copy':
          setCopyState({ sourceNode: node });
          break;
        case 'remove':
          setRemovingNode(node);
          break;
        default: {
          const _exhaustive: never = action;
          return _exhaustive;
        }
      }
    },
    []
  );

  // Fee tree remove confirmation handlers
  const handleRemoveCancel = useCallback((): void => {
    if (isRemoving) return;
    setRemovingNode(null);
  }, [isRemoving]);

  const handleRemoveConfirm = useCallback(async (): Promise<void> => {
    if (!removingNode || removingNode.feeConfigIds.length === 0) return;

    setIsRemoving(true);
    try {
      let anySucceeded = false;
      for (const configId of removingNode.feeConfigIds) {
        const success = await removeFeeConfiguration(configId);
        if (success) anySucceeded = true;
      }

      if (anySucceeded) {
        if (selectedFeeNodeId === removingNode.id) {
          setSelectedFeeNodeId(null);
        }
        showToast({
          title: 'Fee configuration removed',
          description: `"${removingNode.label}" has been archived.`,
          color: 'success',
        });
      } else {
        showToast({
          title: 'Failed to remove fee configuration',
          description: 'The configuration may have already been removed.',
          color: 'danger',
        });
      }
    } catch (error) {
      console.error('Failed to remove fee configuration:', error);
      showToast({
        title: 'Failed to remove fee configuration',
        description: 'An unexpected error occurred. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsRemoving(false);
      setRemovingNode(null);
    }
  }, [removingNode, removeFeeConfiguration, selectedFeeNodeId, showToast]);

  // Fee tree add category success handler
  const handleCategoryAdded = useCallback((nodeId: string): void => {
    setSelectedFeeNodeId(nodeId);
  }, []);

  // Fee tree add instrument success handler
  const handleInstrumentAdded = useCallback((nodeId: string): void => {
    setSelectedFeeNodeId(nodeId);
  }, []);

  // Fee tree copy success handler
  const handleCopyComplete = useCallback((nodeId: string): void => {
    setSelectedFeeNodeId(nodeId);
  }, []);

  // Fee tree header add popover handlers
  const handleAddFeeCategory = useCallback((): void => {
    setIsAddFeePopoverOpen(false);
    setAddCategoryState({ parentCategoryPath: [] });
  }, []);

  const handleAddFeeInstrument = useCallback((): void => {
    setIsAddFeePopoverOpen(false);
    setAddInstrumentState({ parentCategoryPath: [] });
  }, []);

  // ===========================================================================
  // Render Helpers
  // ===========================================================================

  const hasBrokers = brokers.length > 0;
  const hasFilteredBrokers = filteredBrokers.length > 0;

  // ===========================================================================
  // Desktop Layout
  // ===========================================================================

  return (
      <div className="flex flex-col gap-6 w-full h-full min-h-0 overflow-hidden">
        {/* Broker Selector Section */}
        <Card shadow="sm" radius="sm" className={isFeeCardExpanded ? 'hidden' : ''}>
          <CardHeader className="flex flex-row items-center gap-2">
            {/* Broker Select with label */}
            {hasBrokers && (
              <Select
                label="Broker:"
                aria-label="Select broker"
                placeholder="Select broker"
                selectedKeys={selectedBrokerId ? [selectedBrokerId] : []}
                onSelectionChange={handleBrokerSelect}
                isOpen={isSelectOpen}
                onOpenChange={handleSelectOpenChange}
                className="flex w-full"
                isDisabled={loading}
                labelPlacement="outside-left"
                classNames={{
                  label: 'text-lg font-semibold text-text-primary',
                  trigger: 'bg-transparent shadow-none data-[hover=true]:bg-default-50',
                  value: '!text-text-primary text-lg',
                }}
                listboxProps={{
                  itemClasses: {
                    base: 'data-[hover=true]:!text-primary data-[hover=true]:!bg-transparent',
                  },
                }}
              >
                {[
                  // Active brokers section
                  ...(activeBrokers.length > 0
                    ? [
                        <SelectSection key="active" title="Active" showDivider>
                          {activeBrokers.map((broker) => (
                            <SelectItem key={broker.id}>{broker.name}</SelectItem>
                          ))}
                        </SelectSection>,
                      ]
                    : []),
                  // Actions section (New + Import + Show Archived toggle)
                  <SelectSection
                    key="__actions_section__"
                    showDivider={showArchived && archivedBrokers.length > 0}
                  >
                    <SelectItem
                      key={NEW_BROKER_KEY}
                      startContent={<Plus size={16} />}
                      className="text-secondary"
                    >
                      New Broker
                    </SelectItem>
                    <SelectItem
                      key={IMPORT_CATALOG_KEY}
                      className="text-secondary"
                    >
                      Import from Catalog...
                    </SelectItem>
                    <SelectItem
                      key={TOGGLE_ARCHIVED_KEY}
                      startContent={showArchived ? <Eye size={16} /> : <EyeOff size={16} />}
                    >
                      Archived
                    </SelectItem>
                  </SelectSection>,
                  // Archived brokers section (only when showArchived is on)
                  ...(showArchived && archivedBrokers.length > 0
                    ? [
                        <SelectSection key="archived" title="Archived">
                          {archivedBrokers.map((broker) => (
                            <SelectItem key={broker.id}>{broker.name}</SelectItem>
                          ))}
                        </SelectSection>,
                      ]
                    : []),
                ]}
              </Select>
            )}

            {/* When no brokers, show heading instead */}
            {!hasBrokers && (
              <span className="text-lg font-semibold text-text-primary">Brokers</span>
            )}

            <div className="flex items-center gap-2 ml-auto">
              {/* Rename Button */}
              <Button
                size="sm"
                variant="light"
                isDisabled={!selectedBroker || loading}
                onPress={renameModalDisclosure.onOpen}
              >
                Rename
              </Button>
              {/* Show/Hide Details Toggle */}
              <Button
                size="sm"
                variant="light"
                onPress={() => setIsDetailsExpanded((prev) => !prev)}
              >
                {isDetailsExpanded ? 'Hide Details' : 'Show Details'}
              </Button>
            </div>
          </CardHeader>

          {/* CardBody - rendered when expanded OR loading/empty states need to show */}
          {(isDetailsExpanded ||
            loading ||
            (!loading && (!hasBrokers || !hasFilteredBrokers))) && (
            <CardBody className="space-y-4 border-t border-default-200">
              {/* Empty State - No brokers (always visible) */}
              {!hasBrokers && !loading && (
                <EmptyState
                  title="Create your first broker"
                  description="Brokers help you track trading accounts and fee structures"
                  action={
                    <Popover
                      isOpen={isAddPopoverOpen}
                      onOpenChange={setIsAddPopoverOpen}
                      placement="bottom"
                    >
                      <PopoverTrigger>
                        <Button
                          variant="bordered"
                          startContent={<Plus size={16} />}
                        >
                          New Broker
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="p-1">
                        <div className="flex flex-col min-w-40">
                          <Button
                            size="md"
                            variant="light"
                            className="justify-start"
                            startContent={<Plus size={16} />}
                            onPress={handleAddPopoverCreateNew}
                          >
                            Create New...
                          </Button>
                          <Button
                            size="md"
                            variant="light"
                            className="justify-start"
                            startContent={<Package size={16} />}
                            onPress={handleAddPopoverImport}
                          >
                            Import from Catalog...
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  }
                />
              )}

              {/* Empty State - All archived, filter off (always visible) */}
              {hasBrokers && !hasFilteredBrokers && !loading && (
                <EmptyState
                  title="No active brokers"
                  description="All brokers are archived. Enable 'Show Archived' to view them."
                />
              )}

              {/* Conflict Banner - only when expanded */}
              {isDetailsExpanded && showConflictBanner && (
                <div className="rounded-lg bg-warning-50 border border-warning-200 p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-warning-600 shrink-0" />
                      <span className="text-sm text-warning-700">
                        Broker updated in another tab
                      </span>
                    </div>
                    <div className="flex flex-col gap-2 md:flex-row">
                      <Button size="sm" variant="flat" onPress={keepEditing}>
                        Keep editing
                      </Button>
                      <Button size="sm" color="warning" onPress={acceptExternalChanges}>
                        Accept changes
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Broker Detail Form - only when expanded AND broker selected */}
              {isDetailsExpanded && selectedBroker && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                    {/* Card 1: Basics */}
                    <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
                      <CardHeader className="px-1 pt-0 pb-2 min-h-10 text-sm font-semibold text-text-primary border-b-1 border-default-200">
                        Basics
                      </CardHeader>
                      <CardBody className="px-1 pb-0 space-y-3">
                        {/* Row 1: Account Currency + Minimum Balance */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                          {/* Account Currency */}
                          <div className="min-w-0">
                            <label className="block text-sm font-medium text-text-primary mb-1">
                              Account Currency
                            </label>
                            <Button
                              variant="bordered"
                              className="w-full justify-between font-normal border-default-200"
                              endContent={<Globe className="w-4 h-4 text-text-tertiary shrink-0" />}
                              onPress={() => setShowCurrencyChooser(true)}
                            >
                              {metadataCurrencyInfo ? (
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{metadataCurrencyInfo.symbol}</span>
                                  <span>{metadataCurrencyInfo.name}</span>
                                  <span className="text-text-secondary">({metadataCurrencyCode})</span>
                                </div>
                              ) : (
                                <span>{metadataCurrencyCode}</span>
                              )}
                            </Button>
                          </div>

                          {/* Minimum Balance */}
                          <div className="min-w-0">
                            <NumberInput
                              label="Minimum Balance"
                              labelPlacement="outside"
                              value={toDisplayValue(localFeeMetadata.minimumBalance?.amount, metadataPrecision)}
                              onValueChange={(val) => {
                                setLocalFeeMetadata({
                                  ...localFeeMetadata,
                                  minimumBalance: {
                                    amount: toStorageValue(val, metadataPrecision) ?? 0,
                                    precision: metadataPrecision,
                                    currency: localFeeMetadata.minimumBalance?.currency ?? metadataCurrencyCode,
                                  },
                                });
                              }}
                              endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{metadataCurrencyHint}</span>}
                              formatOptions={{ minimumFractionDigits: metadataPrecision, maximumFractionDigits: metadataPrecision }}
                              classNames={{ input: 'text-base md:text-sm text-right' }}
                              hideStepper
                              isWheelDisabled
                              placeholder="0"
                            />
                          </div>
                        </div>

                        {/* Description */}
                        <TextArea
                          label="Description"
                          labelPlacement='outside-top'
                          placeholder="Brief description of this broker..."
                          value={localDescription}
                          onValueChange={setLocalDescription}
                          minRows={3}
                          maxRows={3}
                          className="[&_textarea]:text-base md:[&_textarea]:text-sm [&_textarea]:pt-2 p-1"
                        />
                      </CardBody>
                    </Card>

                    {/* Card 2: Platform Fees */}
                    <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
                      <CardHeader className="flex items-center justify-between px-1 pt-0 pb-2 min-h-10 border-b-1 border-default-200">
                        <span className="text-sm font-semibold text-text-primary">Platform Fees</span>
                        <Badge
                          content=""
                          color="success"
                          size="sm"
                          isInvisible={!(localOvernightDefault || localConversionDefault)}
                        >
                          <Button
                            variant="bordered"
                            size="sm"
                            startContent={<Settings2 size={14} />}
                            className="border-1"
                            aria-label={
                              localOvernightDefault || localConversionDefault
                                ? 'Financing & FX (configured)'
                                : 'Financing & FX'
                            }
                            onPress={() => setShowFinancingFXModal(true)}
                          >
                            Financing &amp; FX
                          </Button>
                        </Badge>
                      </CardHeader>
                      <CardBody className="px-1 pb-0 space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <NumberInput
                            label="Monthly Fee"
                            labelPlacement="outside"
                            className="min-w-0"
                            value={toDisplayValue(localFeeMetadata.platformFees?.monthlyFee, metadataPrecision)}
                            onValueChange={(val) => {
                              setLocalFeeMetadata({
                                ...localFeeMetadata,
                                platformFees: {
                                  ...localFeeMetadata.platformFees,
                                  monthlyFee: toStorageValue(val, metadataPrecision) ?? 0,
                                },
                              });
                            }}
                            endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{metadataCurrencyHint}</span>}
                            formatOptions={{ minimumFractionDigits: metadataPrecision, maximumFractionDigits: metadataPrecision }}
                            classNames={{ input: 'text-base md:text-sm text-right' }}
                            hideStepper
                            isWheelDisabled
                            placeholder="0"
                          />

                          <NumberInput
                            label="Data Fees"
                            labelPlacement="outside"
                            className="min-w-0"
                            value={toDisplayValue(localFeeMetadata.platformFees?.dataFees, metadataPrecision)}
                            onValueChange={(val) => {
                              setLocalFeeMetadata({
                                ...localFeeMetadata,
                                platformFees: {
                                  ...localFeeMetadata.platformFees,
                                  dataFees: toStorageValue(val, metadataPrecision) ?? 0,
                                },
                              });
                            }}
                            endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{metadataCurrencyHint}</span>}
                            formatOptions={{ minimumFractionDigits: metadataPrecision, maximumFractionDigits: metadataPrecision }}
                            classNames={{ input: 'text-base md:text-sm text-right' }}
                            hideStepper
                            isWheelDisabled
                            placeholder="0"
                          />

                          <NumberInput
                            label="Inactivity Fee"
                            labelPlacement="outside"
                            className="min-w-0"
                            value={toDisplayValue(localFeeMetadata.platformFees?.inactivityFee, metadataPrecision)}
                            onValueChange={(val) => {
                              setLocalFeeMetadata({
                                ...localFeeMetadata,
                                platformFees: {
                                  ...localFeeMetadata.platformFees,
                                  inactivityFee: toStorageValue(val, metadataPrecision) ?? 0,
                                },
                              });
                            }}
                            endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{metadataCurrencyHint}</span>}
                            formatOptions={{ minimumFractionDigits: metadataPrecision, maximumFractionDigits: metadataPrecision }}
                            classNames={{ input: 'text-base md:text-sm text-right' }}
                            hideStepper
                            isWheelDisabled
                            placeholder="0"
                          />
                        </div>

                        {/* Platform Fee Notes */}
                        <TextArea
                          label="Platform Fee Notes"
                          labelPlacement='outside-top'
                          value={localFeeMetadata.platformFees?.notes ?? ''}
                          onValueChange={(val) => {
                            setLocalFeeMetadata({
                              ...localFeeMetadata,
                              platformFees: {
                                ...localFeeMetadata.platformFees,
                                notes: val,
                              },
                            });
                          }}
                          minRows={3}
                          maxRows={3}
                          placeholder="Platform fee notes..."
                          className="[&_textarea]:text-base md:[&_textarea]:text-sm [&_textarea]:pt-2 p-1"
                        />
                      </CardBody>
                    </Card>
                  </div>

                  {/* Catalog Source Indicator */}
                  {selectedBroker.brokerReferenceId && (
                    <div className="rounded-lg bg-default-100 p-3">
                      <p className="text-sm text-text-secondary">
                        Imported from:{' '}
                        <span className="font-medium text-text-primary">
                          {findBrokerById(selectedBroker.brokerReferenceId)?.name ?? 'Unknown Catalog Entry'}
                        </span>
                      </p>
                      {/* Space for future "Update Available" badge and sync actions */}
                    </div>
                  )}

                  {/* Linked Accounts Indicator */}
                  {linkedActiveAccounts.length > 0 && (
                    <div className="rounded-lg bg-default-100 p-3">
                      <p className="text-sm text-text-secondary">
                        Used by:{' '}
                        <span className="font-medium text-text-primary">
                          {linkedActiveAccounts.length <= 3
                            ? linkedActiveAccounts.map((a) => a.name).join(', ')
                            : `${linkedActiveAccounts.slice(0, 3).map((a) => a.name).join(', ')}, and ${linkedActiveAccounts.length - 3} more`}
                        </span>
                      </p>
                    </div>
                  )}

                </div>
              )}

              {/* Loading State */}
              {loading && (
                <div className="text-center py-4">
                  <p className="text-text-secondary">Loading brokers...</p>
                </div>
              )}
            </CardBody>
          )}

          {/* CardFooter - Only when expanded */}
          {isDetailsExpanded && (
            <CardFooter className="flex items-center justify-between gap-4 border-t border-default-200">
              <div className="flex items-center gap-3">
                {/* Archive/Restore Button */}
                {selectedBroker?.status === 'active' && (
                  <Button
                    variant="flat"
                    size="sm"
                    onPress={archiveBroker}
                    isDisabled={isSaving}
                  >
                    Archive
                  </Button>
                )}
                {selectedBroker?.status === 'archived' && (
                  <Button
                    variant="flat"
                    size="sm"
                    onPress={restoreBroker}
                    isDisabled={isSaving}
                  >
                    Restore
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isDirty && <span className="text-sm text-warning-600">Unsaved changes</span>}
                <Button
                  variant="flat"
                  size="sm"
                  onPress={discardBrokerChanges}
                  isDisabled={!selectedBroker || !isDirty || isSaving}
                >
                  Discard
                </Button>
                <Button
                  color="primary"
                  size="sm"
                  onPress={saveBroker}
                  isDisabled={!selectedBroker || !isDirty || isSaving}
                  isLoading={isSaving}
                >
                  {isDirty && <span className="w-2 h-2 rounded-full bg-white mr-1" />}
                  Save
                </Button>
              </div>
            </CardFooter>
          )}
        </Card>

        {/* Fee Configuration Section */}
        <Card shadow="sm" radius="sm" className="flex-1 min-h-0 flex flex-col">
          <CardHeader className="flex flex-row items-center justify-between">
            <h3 className="text-lg font-semibold text-text-primary">Fee Configuration</h3>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              aria-label={isFeeCardExpanded ? 'Collapse fee configuration' : 'Expand fee configuration'}
              aria-pressed={isFeeCardExpanded}
              onPress={() => setIsFeeCardExpanded(prev => !prev)}
            >
              {isFeeCardExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </Button>
          </CardHeader>
          <CardBody className="flex-1 min-h-0 overflow-hidden border-t border-default-200 px-0">
            {!selectedBroker ? (
              <EmptyState
                title="No broker selected"
                description="Select a broker above to configure fee structures"
              />
            ) : (
              <ResizablePane
                storageKey="brokers-fee-pane-width"
                defaultWidth={256}
                minWidth={100}
                maxWidth="50%"
                leftContent={
                  <Card shadow="none" radius="none" className="flex flex-col h-full min-h-0 overflow-hidden">
                    <CardHeader className="shrink-0 border-b-1 border-default-200 pr-6">
                      <div className="flex gap-2 w-full items-center">
                        <Input
                          placeholder="Search..."
                          size="sm"
                          value={feeSearchQuery}
                          onValueChange={setFeeSearchQuery}
                          startContent={<Search className="w-4 h-4 text-text-secondary" />}
                          className="flex-1"
                          isClearable
                        />
                        <Popover
                          isOpen={isAddFeePopoverOpen}
                          onOpenChange={setIsAddFeePopoverOpen}
                          placement="bottom-end"
                        >
                          <PopoverTrigger>
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              aria-label="Add fee configuration"
                            >
                              <Plus size={16} />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="p-1">
                            <div className="flex flex-col min-w-40">
                              <Button
                                size="md"
                                variant="light"
                                className="justify-start"
                                onPress={handleAddFeeCategory}
                              >
                                Add Fee Category
                              </Button>
                              <Button
                                size="md"
                                variant="light"
                                className="justify-start"
                                onPress={handleAddFeeInstrument}
                              >
                                Add Instrument
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </CardHeader>
                    <CardBody className='flex-1 min-h-0 overflow-hidden pt-1 px-0'>
                      <ScrollShadow hideScrollBar className="h-full">
                        <FeeTree
                          brokerId={selectedBroker.id}
                          feeConfigurations={selectedBroker.feeConfigurations}
                          selectedNodeId={selectedFeeNodeId}
                          onSelectNode={handleFeeNodeSelect}
                          onAction={handleFeeAction}
                          searchQuery={feeSearchQuery}
                        />
                      </ScrollShadow>
                    </CardBody>
                  </Card>
                }
                rightContent={
                  selectedFeeNodeId ? (
                    <FeeConfigurationEditor
                      selectedNodeId={selectedFeeNodeId}
                      feeConfigurations={selectedBroker?.feeConfigurations ?? []}
                      onNodeSelect={handleFeeNodeSelect}
                      isDirtyRef={feeEditorDirtyRef}
                      discardRef={feeEditorDiscardRef}
                      saveRef={feeEditorSaveRef}
                    />
                  ) : (
                    <div className="pl-3 h-full flex items-center justify-center">
                      <EmptyState
                        title="Select a fee configuration"
                        description="Select a fee configuration to view details"
                      />
                    </div>
                  )
                }
              />
            )}
          </CardBody>
        </Card>

        {/* Modals */}
        <BrokerModal
          isOpen={brokerModalDisclosure.isOpen}
          onClose={brokerModalDisclosure.onClose}
          onSave={handleBrokerModalSave}
        />

        {selectedBroker && (
          <BrokerRenameModal
            isOpen={renameModalDisclosure.isOpen}
            onClose={renameModalDisclosure.onClose}
            broker={selectedBroker}
            onSave={handleRenameModalSave}
          />
        )}

        <BrokerImportModal
          isOpen={importModalDisclosure.isOpen}
          onClose={importModalDisclosure.onClose}
          onImportComplete={handleImportComplete}
          existingBrokers={brokers}
          loading={loading}
        />

        {/* Metadata Account Currency Chooser */}
        <CurrencyChooserModal
          isOpen={showCurrencyChooser}
          onClose={() => setShowCurrencyChooser(false)}
          selectedCurrency={metadataCurrencyCode}
          onCurrencySelect={handleMetadataCurrencyChange}
          title="Account Currency"
        />

        {/* Remove Fee Configuration Confirmation Modal */}
        <Modal
          isOpen={!!removingNode}
          onOpenChange={(open) => !open && handleRemoveCancel()}
          isDismissable={!isRemoving}
          hideCloseButton={isRemoving}
          size="sm"
          placement="center"
        >
          <ModalContent>
            <ModalHeader>Remove Fee Configuration</ModalHeader>
            <ModalBody>
              <p className="text-text-secondary">
                Are you sure you want to remove the fee configuration for &quot;{removingNode?.label}&quot;?
              </p>
              <p className="text-sm text-text-tertiary">
                The configuration will be archived and can be restored by adding it again.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={handleRemoveCancel} isDisabled={isRemoving}>
                Cancel
              </Button>
              <Button color="warning" onPress={handleRemoveConfirm} isLoading={isRemoving}>
                Remove
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Unsaved Fee Changes Confirmation Modal */}
        <Modal
          isOpen={pendingNodeNavigation !== null}
          onOpenChange={(open) => !open && handleUnsavedCancel()}
          isDismissable={!isNavSaving}
          hideCloseButton={isNavSaving}
          size="sm"
          placement="center"
        >
          <ModalContent>
            <ModalHeader>Unsaved Changes</ModalHeader>
            <ModalBody>
              <p className="text-text-secondary">
                You have unsaved changes. Save or discard before navigating?
              </p>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={handleUnsavedCancel} isDisabled={isNavSaving}>
                Cancel
              </Button>
              <Button color="warning" onPress={handleUnsavedDiscard} isDisabled={isNavSaving}>
                Discard
              </Button>
              <Button color="primary" onPress={handleUnsavedSave} isLoading={isNavSaving}>
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Add Fee Category Modal */}
        <AddCategoryModal
          isOpen={!!addCategoryState}
          onClose={() => setAddCategoryState(null)}
          parentCategoryPath={addCategoryState?.parentCategoryPath ?? []}
          onAdded={handleCategoryAdded}
        />

        {/* Add Instrument Modal */}
        <AddInstrumentModal
          isOpen={!!addInstrumentState}
          onClose={() => setAddInstrumentState(null)}
          parentCategoryPath={addInstrumentState?.parentCategoryPath ?? []}
          onAdded={handleInstrumentAdded}
        />

        {/* Copy Fee Modal — ref pattern preserves close animation */}
        {lastCopySourceRef.current && (
          <CopyFeeModal
            isOpen={!!copyState}
            onClose={() => setCopyState(null)}
            sourceNode={lastCopySourceRef.current}
            feeConfigurations={selectedBroker?.feeConfigurations ?? []}
            onCopied={handleCopyComplete}
          />
        )}

        {/* Broker-Level Financing & FX Settings Modal */}
        <BrokerFinancingFXModal
          isOpen={showFinancingFXModal}
          onClose={() => setShowFinancingFXModal(false)}
          overnightDefault={localOvernightDefault}
          conversionDefault={localConversionDefault}
          accountCurrency={metadataCurrencyCode}
          onConfirm={handleFinancingFXConfirm}
        />
      </div>
    );
};

// =============================================================================
// Main Export
// =============================================================================

/**
 * BrokersTab Component
 *
 * Wraps the tab content with the BrokersProvider.
 */
export const BrokersTab: React.FC = () => {
  return (
    <BrokersProvider>
      <BrokersTabContent />
    </BrokersProvider>
  );
};
